<template>
    <authenticated-layout>
        <v-card class="px-4">
            <div class="mt-6 mb-4">
                <h2 class="font-bold text-lg text-gray-700">Statistics</h2>

                <div class="grid grid-cols-6 gap-2 mt-3">
                    <StatCard
                        :value="totalNumberOfWonOffers"
                        label="Total number of offers"
                    />

                    <StatCard
                        :value="$numberHelper.formatMoney(totalSalesVolume)"
                        label="Total sales volume"
                    />

                    <StatCard
                        :value="$numberHelper.formatMoney(totalMerchantCommissions)"
                        label="Total merchant commissions"
                    />

                    <StatCard
                        :value="$numberHelper.formatMoney(totalCompanyMargin)"
                        label="Total company margin"
                    />

                    <StatCard
                        :value="`${averageMargin.toFixed(2)} %`"
                        label="Average margin"
                    />

                    <StatCard
                        :value="totalPanelsSold"
                        label="Total panels sold"
                    />
                </div>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-7 gap-2 pt-4">
                <div class="cols-span-2">
                    <v-select
                        v-model="form.salesperson"
                        :label="$t('customers.guardian')"
                        placeholder="Select guardian"
                        :items="[
                            {
                                title: $t('All'),
                                value: null,
                            },
                            ...salespeople.map((o) => ({
                                title: o.name,
                                value: o.id,
                            })),
                        ]"
                        variant="outlined"
                        density="compact"
                        persistent-placeholder
                        hide-details
                    />
                </div>
                <div class="cols-span-1">
                    <v-select
                        v-model="form.offerType"
                        :label="$t('Offer type')"
                        placeholder="Select a type"
                        :items="[
                            {
                                title: $t('All'),
                                value: null,
                            },
                            {
                                title: $t('Solar'),
                                value: 'solar',
                            },
                            {
                                title: $t('Heatpump'),
                                value: 'heatpump',
                            },
                        ]"
                        variant="outlined"
                        density="compact"
                        persistent-placeholder
                        hide-details
                    />
                </div>

                <div class="d-flex align-center col-span-1">
                    <v-text-field
                        v-model="form.search"
                        label="Search"
                        placeholder="Type to search..."
                        variant="outlined"
                        density="compact"
                        persistent-placeholder
                        hide-details
                    />
                </div>

                <div class="d-flex align-center col-span-2">
                    <VueDatePicker
                        :teleport="true"
                        :enable-time-picker="false"
                        text-input v-model="form.datePeriod"
                        format="yyyy-MM-dd"
                        :range="true"
                    />
                </div>

                <div class="d-flex align-center col-span-2">
                    <v-btn
                        variant="flat"
                        class="ml-2"
                        size="small"
                        color="blue"
                        @click="filter()"
                    >
                        {{ $t("sharedSearchFilter.Filter") }}
                    </v-btn>
                    <v-btn
                        variant="flat"
                        class="ml-2"
                        size="small"
                        color="grey"
                        @click="resetFilers()"
                    >
                        {{ $t("sharedSearchFilter.Reset") }}
                    </v-btn>
                </div>
            </div>

            <div class="overflow-x-auto mt-4">
                <table style="table-layout: auto;">
                    <tr>
                        <th class="text-left">
                            <input
                                type="checkbox"
                                v-model="selectAll"
                                @change="selectAllItems"
                            />
                        </th>
                        <th class="text-left">
                            id
                        </th>
                        <th class="text-left">
                            Deal number
                        </th>
                        <th class="text-left">
                            Customer name
                        </th>
                        <th class="text-left">
                            Customer address
                        </th>
                        <th class="text-left">
                            Offer type
                        </th>
                        <th class="text-left">
                            Sales person
                        </th>
                        <th class="text-left">
                            Sales premium
                        </th>
                        <th class="text-left">
                            Selling price
                        </th>
                        <th class="text-left">
                            Price per watt
                        </th>
                        <th class="text-left">
                            Discount
                        </th>
                        <th class="text-left">
                            Merchant Commissions
                        </th>
                        <th class="text-left">
                            Company margin
                        </th>
                        <th class="text-left">
                            Margin
                        </th>
                        <th class="text-left">
                            Won at
                        </th>
                        <th class="text-left">
                            Stage
                        </th>
                        <th class="text-left">
                            &nbsp;
                        </th>
                    </tr>

                    <tr
                        v-for="wonOffer in wonOffers.data"
                        :key="`won-offer-${wonOffer.id}`"
                    >
                        <td>
                            <input
                                type="checkbox"
                                v-model="selectedItems"
                                :value="wonOffer.id"
                            />
                        </td>
                        <td>{{ wonOffer.id }}</td>
                        <td>{{ wonOffer.deal_number }}</td>
                        <td>{{ wonOffer.customer_name }}</td>
                        <td>{{ wonOffer.customer_address }}</td>
                        <td>{{ wonOffer.offer_type }}</td>
                        <td>{{ wonOffer.sales_user_name }}</td>
                        <td>{{ $numberHelper.formatMoney(Math.round(wonOffer.sales_premium)) }}</td>
                        <td>{{ $numberHelper.formatMoney(wonOffer.selling_price) }}</td>
                        <td>{{ $numberHelper.formatMoney(wonOffer.price_per_watt) }}</td>
                        <td>{{ $numberHelper.formatMoney(wonOffer.discount) }}</td>
                        <td>{{ $numberHelper.formatMoney(wonOffer.merchant_commissions) }}</td>
                        <td>{{ $numberHelper.formatMoney(wonOffer.company_margin) }}</td>
                        <td>{{ wonOffer.margin }}%</td>
                        <td>{{ wonOffer.won_at }}</td>
                        <td>{{ wonOffer.pipedrive_stage }}</td>
                        <td>
                            <v-btn
                                variant="flat"
                                size="x-small"
                                color="primary"
                                :href="wonOffer.link_to_offer"
                            >
                            {{ $t("customers.view") }}
                            </v-btn>
                        </td>
                    </tr>
                </table>
            </div>

            <ol
                v-if="selectedItems.length > 1"
                class="my-4 text-sm grid grid-cols-4 overflow-hidden rounded-md"
            >
                <li class="p-3 d-flex justify-space-between align-center bg-gray-50">
                    Sum of sales premium:
                    <b class="ml-3">{{ $numberHelper.formatMoney(getSelectedItemsSum('sales_premium')) }}</b>
                </li>

                <li class="p-3 d-flex justify-space-between align-center bg-gray-100">
                    Sum of selling price:
                    <b class="ml-3">{{ $numberHelper.formatMoney(getSelectedItemsSum('selling_price')) }}</b>
                </li>

                <li class="p-3 d-flex justify-space-between align-center bg-gray-50">
                    Sum of merchant commissions:
                    <b class="ml-3">{{ $numberHelper.formatMoney(getSelectedItemsSum('merchant_commissions')) }}</b>
                </li>

                <li class="p-3 d-flex justify-space-between align-center bg-gray-100">
                    Sum of company margin:
                    <b class="ml-3">{{ $numberHelper.formatMoney(getSelectedItemsSum('company_margin')) }}</b>
                </li>
            </ol>

            <div class="mt-6 pb-6 d-flex align-center">
                <v-select
                    v-model="itemsPerPage"
                    label="Items per page"
                    class="max-w-[150px] mr-5"
                    :items="itemsPerPageOptions"
                    variant="outlined"
                    density="compact"
                    persistent-placeholder
                    hide-details
                />

                <div>
                    <template v-for="(link, k) in wonOffers.links" :key="k">
                        <v-btn
                            v-if="link.url === null"
                            size="small"
                            variant="flat"
                            class="mr-1"
                        >
                            {{ link.label }}
                        </v-btn>

                        <v-btn
                            v-else
                            :href="link.url"
                            size="small"
                            variant="flat"
                            class="mr-1"
                            :color="link.active ? 'primary' : ''"
                        >
                            {{ link.label }}
                        </v-btn>
                    </template>
                </div>
            </div>
        </v-card>
    </authenticated-layout>
</template>

<script>
import AuthenticatedLayout from "@/Layouts/Authenticated.vue";
import StatCard from '@/Components/WonOffer/StatCard.vue';
import pickBy from "lodash/pickBy";

export default {
    components: {
        AuthenticatedLayout,
        StatCard,
    },
    props: {
        filters: Object,
        wonOffers: {
            type: Object,
            required: true,
        },
        salespeople: {
            type: Array,
            required: true,
        },
        selectedSalesperson: {
            type: Number,
            required: false,
        },
        pagelimit: {
            type: Number,
            required: true,
        },
        totalNumberOfWonOffers: {
            type: Number,
            required: true,
        },
        totalSalesVolume: {
            type: Number,
            required: true,
        },
        totalMerchantCommissions: {
            type: Number,
            required: true,
        },
        totalCompanyMargin: {
            type: Number,
            required: true,
        },
        averageMargin: {
            type: Number,
            required: true,
        },
        totalPanelsSold: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            form: {
                search: this.filters.search,
                salesperson: this.filters.salesperson,
                offerType: this.filters.offerType,
                datePeriod: this.filters.datePeriod,
            },
            selectAll: false,
            selectedItems: [],
            itemsPerPage: this.pagelimit,
            itemsPerPageOptions: [20, 30, 50, 100, 500, 1000],
        };
    },
    watch: {
        itemsPerPage() {
            this.onPageLimitChange();
        },
    },
    methods: {
        resetFilers() {
            this.form.search = null;
            this.form.salesperson = null;
            this.form.offerType = null;
            this.form.datePeriod = null;

            this.filter();
        },
        filter() {
            this.$inertia.get(this.route('won-offers-index'), pickBy(this.form), {
                preserveState: true,
            });
        },
        selectAllItems() {
            if (this.selectAll) {
                this.selectedItems = this.wonOffers.data.map((item) => item.id);
            } else {
                this.selectedItems = [];
            }
        },
        getSelectedItemsSum(column) {
            return this.selectedItems.reduce((sum, itemId) => {
                const item = this.wonOffers.data.find(item => item.id === itemId);

                return sum + parseFloat(item[column]) || 0;
            }, 0);
        },
        onPageLimitChange() {
            this.$inertia.get(this.route('won-offers-index'), {
                ...pickBy(this.form),
                pageLimit: this.itemsPerPage,
            }, {
                preserveState: true,
            });
        },
    },
};
</script>

<style scoped>
table {
    width: 100%;
    table-layout: fixed;
    font-size: 12px;
}

@media screen and (max-width: 1024px) {
    table {
        width: auto;
    }
}

th {
    padding: 10px 0px;
    text-align: left;
    box-sizing: border-box;
    padding-left: 10px;
    border-bottom: 1px solid #aaa;
}

td {
    text-align: left;
    box-sizing: border-box;
    padding: 5px 0px;
    padding-left: 10px;
    border-bottom: 1px solid #ccc;
}
</style>
